import type { KeyboardEvent, ReactNode } from 'react';
import { Popover } from './Popover';
import { formatPropertyPillLabel } from './propertyBarDisplay';

export interface PropertyPillProps {
  /** Property definition label (e.g. "Owner"). */
  label: string;
  /** Resolved display value; null/empty renders the em-dash placeholder. */
  displayValue: string | null;
  /** Optional subtype-default marker (e.g. ✦) shown after the label text. */
  marker?: string;
  /** Stable test id for the marker span when `marker` is set. */
  markerTestId?: string;
  /** Stable test id for the pill trigger. */
  testId?: string;
  /** When false, pill is read-only preview — no popover. Default true. */
  editable?: boolean;
  /** Popover body — same pickers as the inspector property row. */
  children: (api: { close: () => void }) => ReactNode;
}

function isEmptyDisplayValue(displayValue: string | null): boolean {
  return displayValue == null || displayValue === '';
}

function pillClassName(displayValue: string | null): string {
  return [
    'pn-property-pill',
    isEmptyDisplayValue(displayValue) ? 'pn-property-pill-muted' : null
  ]
    .filter(Boolean)
    .join(' ');
}

function openPopoverOnKey(
  event: KeyboardEvent<HTMLButtonElement>,
  onOpen: () => void
): void {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  onOpen();
}

/**
 * Focusable labeled property pill for **typed** canvas-bar fields (`text`,
 * `number`, `url`, `date`, `multi_select`). Opens a minimal popover with a
 * compact control. Picker kinds (`user`, `single_select`) use
 * `UserPicker` / `PickOrCreate` with `triggerVariant="pill"` instead — one
 * click opens the dropdown directly.
 */
function PillLabel({
  text,
  marker,
  markerTestId
}: {
  text: string;
  marker?: string;
  markerTestId?: string;
}) {
  if (!marker) return text;
  return (
    <>
      {text}{' '}
      <span
        className="pn-property-pill-marker"
        data-testid={markerTestId}
        aria-hidden="true"
      >
        {marker}
      </span>
    </>
  );
}

export function PropertyPill({
  label,
  displayValue,
  marker,
  markerTestId,
  testId,
  editable = true,
  children
}: PropertyPillProps) {
  const text = formatPropertyPillLabel(label, displayValue);
  const cls = pillClassName(displayValue);
  const content = (
    <PillLabel text={text} marker={marker} markerTestId={markerTestId} />
  );

  if (!editable) {
    return (
      <button
        type="button"
        className={cls}
        data-testid={testId}
        aria-disabled="true"
      >
        {content}
      </button>
    );
  }

  return (
    <Popover
      align="start"
      strategy="fixed"
      label={label}
      trigger={(api) => (
      <button
        type="button"
        className={cls}
        data-testid={testId}
        onClick={api.onClick}
        onKeyDown={(event) => openPopoverOnKey(event, api.onClick)}
        aria-haspopup={api['aria-haspopup']}
        aria-expanded={api['aria-expanded']}
      >
        {content}
      </button>
    )}>
      {children}
    </Popover>
  );
}
